import { Box } from '@chakra-ui/react/box';
import { Group } from '@chakra-ui/react/group';
import { Input, type InputProps } from '@chakra-ui/react/input';
import { InputAddon, type InputAddonProps } from '@chakra-ui/react/input-addon';
import { useSlotRecipe } from '@chakra-ui/react/styled-system';
import { type ChangeEventHandler, forwardRef, type ReactNode } from 'react';

import { IconErrorCircleFilled, IconWarningYellow } from '../../icons';
import BitkitField, { type BitkitFieldProps } from '../BitkitField/BitkitField';

export interface BitkitTextInputProps extends Omit<BitkitFieldProps, 'children' | 'onChange' | 'state' | 'value'> {
  /**
   * Show a live character counter (`length/maxLength`) next to the label. Requires `maxLength` and a
   * known `value`.
   */
  counter?: boolean;
  /**
   * Content attached to the trailing edge, outside the input border. A bare string or number
   * renders in the standard bordered addon box (a unit label like `USD`); any other node renders
   * as itself — see `startAddon`.
   */
  endAddon?: ReactNode;
  /** Props forwarded to the trailing `InputAddon` box. Inert unless the addon is a string. */
  endAddonProps?: InputAddonProps;
  /** Inline content rendered inside the input, at the trailing edge (e.g. an icon or reveal button). */
  endElement?: ReactNode;
  /**
   * Escape hatch for the underlying Chakra `Input` — extra native attributes or a spread
   * `{...register()}`. On conflict, values here win over the top-level `value`/`onChange`.
   */
  inputProps?: InputProps;
  /** Maximum number of characters accepted; also feeds the optional `counter`. */
  maxLength?: HTMLInputElement['maxLength'];
  /**
   * Native change event — the same handler you could put on `inputProps`, promoted to the top level.
   * Reach for `onValueChange` when you only need the value. If both this and `inputProps.onChange` are
   * set, `inputProps.onChange` wins (it's the same event, so they shouldn't both be set).
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** Convenience change handler that receives the value directly, without `event.target.value`. */
  onValueChange?: (value: string) => void;
  /** Placeholder text shown while the field is empty. */
  placeholder?: string;
  /** Field size — controls height and typography. */
  size?: 'md' | 'lg';
  /**
   * Content attached to the leading edge, outside the input border. Text has to arrive as a bare
   * string (or number) to get the standard bordered addon box (a unit label like `$`) — wrapped in
   * a `<Text>` or a fragment it counts as a component, which loses the box and leaves
   * `startAddonProps` inert. Any other node keeps its own styling and is welded to the field
   * instead: it shares the field's border and the corners it touches are squared automatically, so
   * a `BitkitButton` / `BitkitIconButton` needs only its own `variant` and a `size` matching the
   * field's — the field's `size` does not reach it.
   */
  startAddon?: ReactNode;
  /** Props forwarded to the leading `InputAddon` box. Inert unless the addon is a string. */
  startAddonProps?: InputAddonProps;
  /** Inline content rendered inside the input, at the leading edge (e.g. an icon). */
  startElement?: ReactNode;
  /**
   * Field state. `error`/`warning` are also implied by `errorText`/`warningText`; `disabled` and
   * `readOnly` gate interaction.
   */
  state?: 'disabled' | 'error' | 'readOnly' | 'warning';
  /**
   * Controlled value. Pair with `onValueChange` or `onChange`. Can also be supplied via `inputProps`,
   * which wins on conflict.
   */
  value?: string;
}

/**
 * A single-line text field wrapping the native `<input>`, with a label and helper/error/warning
 * text. Reflect the value with `value` + `onValueChange` (or the native `onChange`); `inputProps` is
 * the escape hatch for any other native attribute. `startAddon`/`endAddon` sit outside the border
 * (as a bordered box for text, welded to the field for a component), `startElement`/`endElement`
 * inline inside it, and `counter` pairs with `maxLength`.
 */
const BitkitTextInput = forwardRef<HTMLDivElement, BitkitTextInputProps>((props, ref) => {
  const {
    counter,
    endAddon,
    endAddonProps,
    endElement,
    inputProps,
    maxLength,
    onChange,
    onValueChange,
    placeholder,
    size = 'lg',
    startAddon,
    startAddonProps,
    startElement,
    state,
    value,
    ...rest
  } = props;

  const recipe = useSlotRecipe({ key: 'textInput' });
  const styles = recipe({ size });

  const iconSize = size === 'md' ? '16' : '24';
  const addonSize = size === 'md' ? 'md' : 'lg';

  const hasWarning = state === 'warning' || !!rest.warningText;
  const isInvalid = state === 'error' || !!rest.errorText;

  let statusIcon: ReactNode = null;
  if (isInvalid) {
    statusIcon = <IconErrorCircleFilled color="icon/negative" size={iconSize} />;
  } else if (hasWarning) {
    statusIcon = <IconWarningYellow size={iconSize} />;
  }

  // `value` / `onChange` may come in at the top level or via `inputProps` — the object wins on
  // conflict (natural for a spread `{...register()}`). `onValueChange` always fires with the value.
  const { css: inputCss, onChange: inputOnChange, value: inputValue, ...restInputProps } = inputProps ?? {};
  const resolvedValue = inputValue ?? value;
  const nativeOnChange = inputOnChange ?? onChange;
  const handleChange: ChangeEventHandler<HTMLInputElement> | undefined =
    onValueChange || nativeOnChange
      ? (event) => {
          onValueChange?.(event.target.value);
          nativeOnChange?.(event);
        }
      : undefined;

  const counterText =
    counter && maxLength && resolvedValue !== undefined ? `${resolvedValue.toString().length}/${maxLength}` : undefined;

  const stateAttrs = {
    'data-disabled': state === 'disabled' ? '' : undefined,
    'data-invalid': isInvalid ? '' : undefined,
    'data-readonly': state === 'readOnly' ? '' : undefined,
  };

  const renderAddon = (addon: ReactNode, position: 'end' | 'start', addonProps?: InputAddonProps) =>
    typeof addon === 'string' || typeof addon === 'number' ? (
      <InputAddon position={position} size={addonSize} {...stateAttrs} {...addonProps}>
        {addon}
      </InputAddon>
    ) : (
      addon
    );

  return (
    <BitkitField counterText={counterText} ref={ref} state={state} {...rest}>
      <Group attached width="100%">
        {startAddon != null && renderAddon(startAddon, 'start', startAddonProps)}
        <Box css={styles.field} data-has-end-element={endElement != null ? '' : undefined} flex="1" {...stateAttrs}>
          {startElement != null && (
            <Box css={styles.element} {...stateAttrs}>
              {startElement}
            </Box>
          )}
          <Input
            css={[styles.input, inputCss]}
            maxLength={maxLength}
            onChange={handleChange}
            placeholder={placeholder}
            value={resolvedValue}
            {...restInputProps}
            unstyled
          />
          {statusIcon != null && (
            <Box css={styles.element} {...stateAttrs}>
              {statusIcon}
            </Box>
          )}
          {endElement != null && (
            <Box css={styles.element} {...stateAttrs}>
              {endElement}
            </Box>
          )}
        </Box>
        {endAddon != null && renderAddon(endAddon, 'end', endAddonProps)}
      </Group>
    </BitkitField>
  );
});

BitkitTextInput.displayName = 'BitkitTextInput';

export default BitkitTextInput;
